/**
 * CONTRATO (usado pela exportação): `waitForFrameRendered()` resolve quando o
 * quadro atual de todas as camadas GOES visíveis está decodificado e na tela
 * (ou, se a imagem daquele horário está indisponível, a camada foi escondida),
 * os módulos registrados avisaram que estão prontos e o mapa terminou de
 * renderizar (`rendercomplete`).
 *
 * Se não conseguir em `timeoutMs` de tempo VISÍVEL (com a aba oculta o
 * navegador não desenha; a contagem pausa até ela voltar), rejeita com
 * `FrameRenderTimeoutError` — nunca resolve com um quadro velho na tela.
 *
 * Outros módulos podem registrar uma verificação extra com
 * `registerFrameReadiness(() => Promise<void> | void)`.
 */
import { useMapInstance } from '../map/mapContext';
import type { GoesLayerManager } from '../map/goesLayers';

let manager: GoesLayerManager | null = null;
const readinessChecks = new Set<() => Promise<void> | void>();

/** O quadro atual não terminou de desenhar dentro do tempo-limite. */
export class FrameRenderTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(timeoutMs: number) {
    super(`O quadro não terminou de desenhar em ${Math.round(timeoutMs / 1000)} s.`);
    this.name = 'FrameRenderTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/** (app-core) Registra o gerenciador das camadas GOES. */
export function setGoesManager(m: GoesLayerManager | null): void {
  manager = m;
}

/** Registra uma verificação de prontidão do quadro atual. Retorna o cancelamento. */
export function registerFrameReadiness(check: () => Promise<void> | void): () => void {
  readinessChecks.add(check);
  return () => {
    readinessChecks.delete(check);
  };
}

/** Resolve após o próximo `rendercomplete` do mapa. */
function nextRenderComplete(): Promise<void> {
  const map = useMapInstance.getState().map;
  if (!map) return Promise.resolve();
  return new Promise((resolve) => {
    map.once('rendercomplete', () => resolve());
    map.render();
  });
}

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Tempo-limite que só conta enquanto a aba está visível. Retorna a promessa
 * (rejeita ao estourar) e a função de cancelar.
 */
function visibleTimeout(ms: number): { promise: Promise<never>; cancel: () => void } {
  let remaining = ms;
  let startedAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let rejectFn: (e: Error) => void = () => {};
  const promise = new Promise<never>((_, reject) => {
    rejectFn = reject;
  });
  const start = () => {
    if (timer || remaining <= 0) return;
    startedAt = Date.now();
    timer = setTimeout(() => rejectFn(new FrameRenderTimeoutError(ms)), remaining);
  };
  const stop = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
    remaining -= Date.now() - startedAt;
  };
  const onVisibility = () => (isHidden() ? stop() : start());
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
  if (!isHidden()) start();
  return {
    promise,
    cancel: () => {
      stop();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

export async function waitForFrameRendered(timeoutMs = 30_000): Promise<void> {
  const timeout = visibleTimeout(timeoutMs);
  timeout.promise.catch(() => {}); // evita "unhandled rejection" quando o trabalho termina antes
  try {
    await Promise.race([
      (async () => {
        if (manager) await manager.whenCurrentFrameReady();
        await Promise.all([...readinessChecks].map((check) => check()));
        await nextRenderComplete();
      })(),
      timeout.promise,
    ]);
  } finally {
    timeout.cancel();
  }
}
